import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, combineLatest, skip, Subscription } from 'rxjs';
import {
  DistanceVerticals,
  VerticalType,
} from 'src/app/enum/vertical-type/verticalType.enum';
import {
  HeaderTableInfo,
  poolPriceComparator,
  poolProfitMarginComparator,
  ProductData,
  productDescriptionComparator,
  ProductInformation,
} from 'src/app/models/pool-parameters/pool-parameters.model';
import { EditProductParametersModalComponent } from '../edit-product-parameters-modal/edit-product-parameters-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { HeaderKey, HeaderTooltipKey } from '../../pool-parameters.component';
import { tuiHintOptionsProvider } from '@taiga-ui/core';

interface UpdateAvailbleObj {
  event: boolean;
  verticalType: VerticalType;
}

@Component({
  selector: 'app-products-table-parameters',
  templateUrl: './products-table-parameters.component.html',
  styleUrls: ['./products-table-parameters.component.css'],
  providers: [
    tuiHintOptionsProvider({
      icon: 'tuiIconInfo',
    }),
  ],
})
export class ProductsTableParametersComponent {
  HeaderKey = HeaderKey;
  DistanceVerticals = DistanceVerticals;

  @Input() productsDetails: ProductInformation = {
    productInformationData: [],
    currencySymbol: '',
  };

  @Input() activeZone: string = '';

  @Output() updateAvailableEmitter = new EventEmitter<UpdateAvailbleObj>();
  @Output() updateSubproductDataEmitter = new EventEmitter<ProductData>();

  ProductsRangeHeaders: HeaderTableInfo[] = [];
  ProductsHeaders: HeaderTableInfo[] = [];
  // ProductsRangeHeadersKeys: (keyof ProductData)[] = [];
  ProductsHeadersKeys: (keyof ProductData)[] = [];

  sortDirections: Record<number, BehaviorSubject<1 | -1>> = {};
  sortKeys: Record<number, BehaviorSubject<keyof ProductData | any>> = {};
  sortedProducts: Record<number, BehaviorSubject<ProductData[]>> = {};
  sortDirectionSubscriptions: Record<number, Subscription> = {};
  sortKeySubscriptions: Record<number, Subscription> = {};

  constructor(
    private modalService: BsModalService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadHeaders();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['productsDetails'] && this.productsDetails) {
      this.setSortedPrducts();
    }
  }

  ngOnDestroy() {
    Object.keys(this.sortDirectionSubscriptions).forEach((key) => {
      this.sortDirectionSubscriptions[Number(key)].unsubscribe();
    });

    Object.keys(this.sortKeySubscriptions).forEach((key) => {
      this.sortKeySubscriptions[Number(key)].unsubscribe();
    });
  }

  updateAvailability(event: boolean, verticalType: VerticalType) {
    this.updateAvailableEmitter.emit({ event, verticalType });
  }

  openUpdateProductModal(
    productData: ProductData,
    index: number,
    productIndex: number,
    verticalsType: VerticalType
  ) {
    
    const config = {
      class: 'modal-class-template-400',
      backdrop: true,
      initialState: {
        productData,
        PrincipalProductName: this.translate.instant(
          'VERTICAL_TYPE.' + verticalsType
        ),
        // Pasamos la currencySymbol tal y como viene en productsDetails
        currencySymbol: this.productsDetails.currencySymbol,
      },
    };
    const modalRef = this.modalService.show(
      EditProductParametersModalComponent,
      config
    );

    modalRef?.content?.onClose.subscribe((result) => {
      if (!result) return;
      this.updateSubproductDataEmitter.emit(result);
    });
  }

  sortProductData(verticalType: VerticalType): void {
    const productList = this.productsDetails.productInformationData.find(
      (p) => p.verticalType === verticalType
    )?.productData;
    if (!productList) return;

    const key = this.sortKeys[verticalType].value;
    const direction = this.sortDirections[verticalType].value;

    const sorted = [...productList].sort((a: any, b: any) => {
      const aValue = a[key];
      const bValue = b[key];
      if (aValue == null || bValue == null) return 0;
      const result = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return direction === 1 ? result : -result;
    });

    this.sortedProducts[verticalType].next(sorted);
  }

  setSortedPrducts(): void {
    if (this.productsDetails?.productInformationData) {
      this.productsDetails.productInformationData.forEach((product) => {
        const vt = product.verticalType;
        this.sortDirections[vt] = new BehaviorSubject<1 | -1>(1);
        this.sortKeys[vt] = new BehaviorSubject<keyof ProductData | any>(
          'productDescription'
        );
        this.sortedProducts[vt] = new BehaviorSubject<ProductData[]>([
          ...product.productData,
        ]);

        // this.sortProductData(vt);

        this.sortDirectionSubscriptions[vt] = this.sortDirections[vt]
          .pipe(skip(1))
          .subscribe(() => {
            this.sortProductData(vt);
          });

        this.sortKeySubscriptions[vt] = this.sortKeys[vt]
          .pipe(skip(1))
          .subscribe(() => {
            this.sortProductData(vt);
          });
      });
    }
  }

  private loadHeaders(): void {
    combineLatest([
      this.translate.stream([
        'pool_parameters.PRODUCT_HEADERS.0',
        'pool_parameters.PRODUCT_HEADERS.1',
        'pool_parameters.PRODUCT_HEADERS.2',
        'pool_parameters.PRODUCT_HEADERS.3',
        'pool_parameters.PRODUCT_HEADERS.4',
      ]),
    ]).subscribe(([product]) => {
      // this.ProductsRangeHeaders = [
      //   {
      //     Title: product['POOL_PARAMETERS.PRODUCT_HEADERS.0'],
      //     Sticky: true,
      //     Key: HeaderKey.RANGE_KM,
      //     Class: 'th-style',
      //     Size: 'm',
      //     Sortable: productDescriptionComparator,
      //   },
      //   {
      //     Title: product['POOL_PARAMETERS.PRODUCT_HEADERS.2'],
      //     Sticky: true,
      //     Key: HeaderKey.PRICE,
      //     Class: 'th-style',
      //     Size: 'm',
      //     TooltipKey: HeaderTooltipKey.PRICE,
      //     Sortable: poolPriceComparator,
      //   },
      //   {
      //     Title: product['POOL_PARAMETERS.PRODUCT_HEADERS.3'],
      //     Sticky: true,
      //     Key: HeaderKey.MARGIN,
      //     Class: 'th-style',
      //     TooltipKey: HeaderTooltipKey.MARGIN,
      //     Sortable: poolProfitMarginComparator,
      //   },
      //   {
      //     Title: product['POOL_PARAMETERS.PRODUCT_HEADERS.4'],
      //     Sticky: true,
      //     Key: HeaderKey.ACTIONS,
      //     Class: 'th-style',
      //     Size: 'm',
      //     Sortable: null,
      //   },
      // ];

      this.ProductsHeaders = [
        {
          Title: product['pool_parameters.PRODUCT_HEADERS.1'],
          Sticky: true,
          Key: HeaderKey.SUBPRODUCT,
          Class: 'th-style',
          Size: 'm',
          Sortable: productDescriptionComparator,
        },
        {
          Title: product['pool_parameters.PRODUCT_HEADERS.2'],
          Sticky: true,
          Key: HeaderKey.PRICE,
          Class: 'th-style',
          Size: 'm',
          TooltipKey: HeaderTooltipKey.PRICE,
          Sortable: poolPriceComparator,
        },
        {
          Title: product['pool_parameters.PRODUCT_HEADERS.3'],
          Sticky: true,
          Key: HeaderKey.MARGIN,
          Class: 'th-style',
          Size: 'm',
          TooltipKey: HeaderTooltipKey.MARGIN,
          Sortable: poolProfitMarginComparator,
        },
        {
          Title: product['pool_parameters.PRODUCT_HEADERS.4'],
          Sticky: true,
          Key: HeaderKey.ACTIONS,
          Class: 'th-style',
          Size: 'm',
          Sortable: null,
        },
      ];

      this.setHeaderKeys();
    });
  }

  setHeaderKeys() {
    // this.ProductsRangeHeadersKeys = this.ProductsRangeHeaders?.map(
    //   (x: HeaderTableInfo): keyof ProductData => {
    //     return x.Key ?? '';
    //   }
    // );
    this.ProductsHeadersKeys = this.ProductsHeaders?.map(
      (x: HeaderTableInfo): keyof ProductData => {
        return x.Key ?? '';
      }
    );
  }
}
